import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { posApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

export function PosReturnsPage() {
  const qc = useQueryClient();
  const [searchReceipt, setSearchReceipt] = useState('');
  const [foundSale, setFoundSale] = useState<any>(null);
  const [returnLines, setReturnLines] = useState<Record<string, { quantity: number; condition: 'resellable' | 'damaged' }>>({});
  const [refundMethod, setRefundMethod] = useState<'original' | 'cash' | 'store_credit'>('original');
  const [reason, setReason] = useState('');

  const listQuery = useQuery({
    queryKey: ['pos', 'returns'],
    queryFn: () => posApi.listReturns({ limit: 50 }),
  });

  const lookupSale = async () => {
    setFoundSale(null);
    setReturnLines({});
    const matches = await posApi.listSales({ limit: 50 });
    const hit = matches.find((s: any) => s.receipt_number === searchReceipt.trim());
    if (!hit) {
      toast.error(`No sale with receipt ${searchReceipt}`);
      return;
    }
    const full = await posApi.getSale(hit.id);
    if (!['completed', 'partially_refunded'].includes(full.status)) {
      toast.error(`Sale is ${full.status} — cannot return`);
      return;
    }
    setFoundSale(full);
  };

  const processMut = useMutation({
    mutationFn: () => posApi.processReturn({
      originalSaleId: foundSale.id,
      lines: Object.entries(returnLines)
        .filter(([, v]) => v.quantity > 0)
        .map(([origLineId, v]) => ({
          originalSaleLineId: origLineId,
          quantity: v.quantity,
          condition: v.condition,
        })),
      refundMethod,
      reason: reason || 'Customer return',
    }),
    onSuccess: (data) => {
      toast.success(`Refund ${fmtCurrency(data.totalRefund)} — ${data.returnNumber}`);
      qc.invalidateQueries({ queryKey: ['pos', 'returns'] });
      setFoundSale(null);
      setReturnLines({});
      setSearchReceipt('');
      setReason('');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6">
      <PageHeader title="Returns" subtitle="FR-POS-04 — Process returns and refunds" />

      <div className="card p-4 mb-6">
        <h2 className="font-semibold mb-3">New return</h2>
        <div className="flex gap-2 mb-4">
          <input
            className="input max-w-xs"
            placeholder="Original receipt # (e.g. R-000001)"
            value={searchReceipt}
            onChange={(e) => setSearchReceipt(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') lookupSale(); }}
          />
          <button className="btn-primary" onClick={lookupSale} disabled={!searchReceipt.trim()}>
            Look up
          </button>
        </div>

        {foundSale && (
          <SaleReturnForm
            sale={foundSale}
            returnLines={returnLines}
            onLineChange={(lineId, patch) =>
              setReturnLines((p) => {
                const existing = p[lineId] ?? { quantity: 0, condition: 'resellable' as const };
                return { ...p, [lineId]: { ...existing, ...patch } };
              })}
            refundMethod={refundMethod}
            onRefundMethodChange={setRefundMethod}
            reason={reason}
            onReasonChange={setReason}
            onSubmit={() => processMut.mutate()}
            loading={processMut.isPending}
          />
        )}
      </div>

      <h2 className="font-semibold text-lg mb-2">Recent returns</h2>
      {listQuery.isLoading ? <LoadingState /> : listQuery.error ? <ErrorState error={listQuery.error} /> : (
        <div className="card overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Return #</th>
                <th>Date</th>
                <th>Original receipt</th>
                <th>Cashier</th>
                <th>Method</th>
                <th className="text-right">Refund</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {!listQuery.data || listQuery.data.length === 0 ? (
                <tr><td colSpan={7}><EmptyState message="No returns yet." /></td></tr>
              ) : listQuery.data.map((r: any) => (
                <tr key={r.id}>
                  <td className="font-mono text-xs">{r.return_number}</td>
                  <td className="text-xs">{new Date(r.return_date).toLocaleString()}</td>
                  <td className="font-mono text-xs">{r.original_receipt_number}</td>
                  <td>{r.cashier_name}</td>
                  <td className="uppercase text-xs">{r.refund_method}</td>
                  <td className="text-right font-semibold">{fmtCurrency(parseFloat(r.total_refund))}</td>
                  <td>
                    <span className={`badge ${r.status === 'completed' ? 'bg-sage-50 text-sage-700 ring-sage-200' : 'bg-canvas-subtle text-ink-700 ring-ink-200'}`}>
                      {r.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SaleReturnForm({
  sale, returnLines, onLineChange, refundMethod, onRefundMethodChange, reason, onReasonChange, onSubmit, loading,
}: {
  sale: any;
  returnLines: Record<string, { quantity: number; condition: 'resellable' | 'damaged' }>;
  onLineChange: (lineId: string, patch: Partial<{ quantity: number; condition: 'resellable' | 'damaged' }>) => void;
  refundMethod: 'original' | 'cash' | 'store_credit';
  onRefundMethodChange: (m: 'original' | 'cash' | 'store_credit') => void;
  reason: string;
  onReasonChange: (s: string) => void;
  onSubmit: () => void;
  loading: boolean;
}) {
  // Compute preview refund
  let refundPreview = 0;
  for (const ln of sale.lines) {
    const returning = returnLines[ln.id]?.quantity ?? 0;
    if (returning > 0) {
      const proportion = returning / parseFloat(ln.quantity);
      refundPreview += parseFloat(ln.line_total) * proportion;
    }
  }
  refundPreview = Math.round(refundPreview * 100) / 100;
  const anyLines = Object.values(returnLines).some((l) => l.quantity > 0);

  return (
    <div>
      <div className="bg-slate-50 border border-slate-200 rounded p-3 mb-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div><span className="text-slate-500">Receipt:</span> <span className="font-mono">{sale.receipt_number}</span></div>
        <div><span className="text-slate-500">Date:</span> {new Date(sale.sale_date).toLocaleString()}</div>
        <div><span className="text-slate-500">Customer:</span> {sale.customer_name ?? 'Walk-in'}</div>
        <div><span className="text-slate-500">Total:</span> <span className="font-semibold">{fmtCurrency(parseFloat(sale.grand_total))}</span></div>
      </div>

      <table className="table mb-4">
        <thead>
          <tr>
            <th>Item</th>
            <th className="text-right">Sold qty</th>
            <th className="text-right">Unit price</th>
            <th className="text-right">Line total</th>
            <th className="text-right">Return qty</th>
            <th>Condition</th>
          </tr>
        </thead>
        <tbody>
          {sale.lines.map((ln: any) => (
            <tr key={ln.id}>
              <td>
                <div className="font-medium">{ln.description}</div>
              </td>
              <td className="text-right">{parseFloat(ln.quantity).toFixed(0)}</td>
              <td className="text-right">{fmtCurrency(parseFloat(ln.unit_price))}</td>
              <td className="text-right">{fmtCurrency(parseFloat(ln.line_total))}</td>
              <td className="text-right">
                <input
                  type="number"
                  min={0}
                  max={parseFloat(ln.quantity)}
                  step={1}
                  className="input w-20 text-right"
                  value={returnLines[ln.id]?.quantity ?? 0}
                  onChange={(e) => onLineChange(ln.id, {
                    quantity: Math.max(0, Math.min(parseFloat(ln.quantity), parseFloat(e.target.value) || 0)),
                  })}
                />
              </td>
              <td>
                <select
                  className="input"
                  value={returnLines[ln.id]?.condition ?? 'resellable'}
                  onChange={(e) => onLineChange(ln.id, { condition: e.target.value as any })}
                  disabled={(returnLines[ln.id]?.quantity ?? 0) === 0}
                >
                  <option value="resellable">Resellable</option>
                  <option value="damaged">Damaged</option>
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div>
          <label className="label">Refund method</label>
          <select className="input" value={refundMethod} onChange={(e) => onRefundMethodChange(e.target.value as any)}>
            <option value="original">Original payment method</option>
            <option value="cash">Cash</option>
            <option value="store_credit">Store credit</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="label">Reason</label>
          <input
            className="input"
            placeholder="e.g. customer unhappy with color"
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
          />
        </div>
      </div>

      <div className="flex items-center justify-between bg-sage-50 border border-brand-200 rounded p-3">
        <div>
          <div className="text-xs text-slate-500">Refund preview</div>
          <div className="text-2xl font-bold">{fmtCurrency(refundPreview)}</div>
        </div>
        <button
          className="btn-primary"
          disabled={!anyLines || loading}
          onClick={onSubmit}
        >
          {loading ? 'Processing…' : `Process refund`}
        </button>
      </div>
    </div>
  );
}
